<template>
	<!-- The district's symbol library and the defaults the map draws with,
	     embedded as a section of the Organization settings page (the standalone
	     Symbology page was removed): a symbol is district configuration, like the
	     tags it is bound to. The member's own view of these styles is under
	     Configuration → Preferences. -->
	<div>
		<div
			class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4"
		>
			<div>
				<p class="text-body-secondary mb-0">
					The district's symbol library and the defaults the map draws with. A
					symbol states the shape and paint; binding one to a tag or to a
					subject kind is what puts it on the map.
				</p>
			</div>
			<ButtonLoading
				text="New symbol"
				icon="bi bi-plus-lg"
				variant="primary"
				@click="openNew"
			/>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<!-- One editor, used for both writes: a new symbol (POST) and a restyle of
		     an existing one (PUT). The fields are the same, so the panel is too. -->
		<div v-if="editorOpen" class="card mb-4">
			<div class="card-body">
				<h2 class="h5 mb-3">
					{{ editorID === null ? "New symbol" : `Restyle “${form.name}”` }}
				</h2>
				<div class="row g-3">
					<div class="col-12 col-md-4">
						<label class="form-label" for="symbol-name">Name</label>
						<input id="symbol-name" v-model="form.name" class="form-control" />
					</div>
					<div class="col-12 col-md-4">
						<label class="form-label" for="symbol-shape">Shape</label>
						<select id="symbol-shape" v-model="form.shape" class="form-select">
							<option v-for="shape in SHAPES" :key="shape" :value="shape">
								{{ shape }}
							</option>
						</select>
						<p class="form-text mb-0">
							The marker a point draws with; an area subject's fill and outline
							are the symbol itself.
						</p>
					</div>
					<div class="col-12 col-md-4">
						<label class="form-label" for="symbol-size">
							Marker size (px)
						</label>
						<input
							id="symbol-size"
							v-model.number="form.size"
							class="form-control"
							type="number"
							min="0"
							max="256"
							step="1"
						/>
						<p class="form-text mb-0">0 leaves the map's own marker size.</p>
					</div>
					<div class="col-12">
						<label class="form-label" for="symbol-description">
							Description
						</label>
						<input
							id="symbol-description"
							v-model="form.description"
							class="form-control"
						/>
					</div>
					<div class="col-12 col-md-6">
						<div class="d-flex align-items-center gap-2">
							<input
								id="symbol-use-fill"
								v-model="form.useFill"
								class="form-check-input mt-0"
								type="checkbox"
							/>
							<label class="form-check-label" for="symbol-use-fill">
								Fill
							</label>
							<input
								v-model="form.fillColor"
								class="form-control form-control-color form-control-sm"
								type="color"
								:disabled="!form.useFill"
								title="The fill colour"
							/>
							<input
								v-model.number="form.fillOpacity"
								class="form-control form-control-sm fill-opacity-input"
								type="number"
								min="0"
								max="1"
								step="0.05"
								:disabled="!form.useFill"
								title="Fill opacity, 0 to 1"
							/>
						</div>
					</div>
					<div class="col-12 col-md-6">
						<div class="d-flex align-items-center gap-2">
							<input
								id="symbol-use-stroke"
								v-model="form.useStroke"
								class="form-check-input mt-0"
								type="checkbox"
							/>
							<label class="form-check-label" for="symbol-use-stroke">
								Outline
							</label>
							<input
								v-model="form.strokeColor"
								class="form-control form-control-color form-control-sm"
								type="color"
								:disabled="!form.useStroke"
								title="The outline colour"
							/>
							<input
								v-model.number="form.strokeWidth"
								class="form-control form-control-sm fill-opacity-input"
								type="number"
								min="0"
								max="32"
								step="0.5"
								title="Outline width in pixels"
							/>
							<input
								v-model.number="form.strokeOpacity"
								class="form-control form-control-sm fill-opacity-input"
								type="number"
								min="0"
								max="1"
								step="0.05"
								title="Outline opacity, 0 to 1"
							/>
						</div>
					</div>
					<div class="col-12">
						<span class="form-label d-block">Icon (optional)</span>
						<IconUpload v-model="form.iconUuid" />
					</div>
					<div v-if="editorID === null" class="col-12">
						<div class="form-check">
							<input
								id="symbol-private"
								v-model="form.private"
								class="form-check-input"
								type="checkbox"
							/>
							<label class="form-check-label" for="symbol-private">
								Keep this in my private library
							</label>
							<p class="form-text mb-0">
								A private symbol is yours alone: it can style your own view of
								the map, but it can never be bound as the district's answer.
							</p>
						</div>
					</div>
				</div>
				<p class="form-text">
					A symbol must state a fill, an outline or an icon — the server is the
					one that decides, so an empty style is refused rather than guessed at.
				</p>
				<div class="d-flex gap-2">
					<ButtonLoading
						:text="editorID === null ? 'Create symbol' : 'Save symbol'"
						icon="bi bi-check-lg"
						variant="primary"
						:loading="saving"
						@click="saveSymbol"
					/>
					<ButtonLoading
						text="Cancel"
						variant="outline-secondary"
						:disabled="saving"
						@click="closeEditor"
					/>
				</div>
			</div>
		</div>

		<div class="card mb-4">
			<div
				class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2 pb-0"
			>
				<h2 class="h5 mb-0">Library</h2>
				<div class="d-flex align-items-center gap-3">
					<span class="text-body-secondary small">
						{{ districtSymbols.length }} district ·
						{{ symbols.length - districtSymbols.length }} private
					</span>
					<div class="form-check form-switch mb-0">
						<input
							id="show-retired-symbols"
							v-model="showRetired"
							class="form-check-input"
							type="checkbox"
						/>
						<label class="form-check-label small" for="show-retired-symbols">
							Show retired
						</label>
					</div>
				</div>
			</div>
			<div class="card-body">
				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div
					v-else-if="visibleSymbols.length === 0"
					class="text-center py-4 text-muted"
				>
					No symbols yet. A symbol is a reusable style: create one above and
					bind it to a tag or to a subject kind.
				</div>

				<div v-else class="row g-3">
					<div
						v-for="symbol in visibleSymbols"
						:key="symbol.id"
						class="col-12 col-md-6 col-lg-4"
					>
						<div
							class="card h-100 border-0 shadow-sm"
							:class="{ 'bg-light': symbol.retired }"
						>
							<div class="card-body d-flex flex-column">
								<div class="d-flex align-items-center gap-2 mb-2">
									<span
										class="symbol-swatch"
										:style="styleCss(symbol)"
										aria-hidden="true"
									></span>
									<img
										v-if="symbolIconURL(symbol.icon_uuid)"
										:src="symbolIconURL(symbol.icon_uuid) || ''"
										class="symbol-icon"
										:alt="symbol.name"
									/>
									<span class="fw-semibold text-truncate">
										{{ symbol.name }}
									</span>
									<span
										class="badge"
										:class="
											symbol.owner === 'district'
												? 'text-bg-primary'
												: 'text-bg-secondary'
										"
									>
										{{ symbol.owner === "district" ? "district" : "private" }}
									</span>
									<span
										v-if="symbol.retired"
										class="badge text-bg-light text-dark"
									>
										Retired
									</span>
								</div>
								<p class="small text-body-secondary mb-1">
									{{ symbol.shape }} · in use by
									{{ symbol.usage_count.toLocaleString() }}
								</p>
								<p
									v-if="symbol.description"
									class="small text-body-secondary mb-2"
								>
									{{ symbol.description }}
								</p>
								<div class="mt-auto d-flex flex-wrap gap-1">
									<ButtonLoading
										text="Restyle"
										variant="outline-primary"
										size="sm"
										@click="openRestyle(symbol)"
									/>
									<ButtonLoading
										v-if="!symbol.retired"
										text="Retire"
										variant="outline-secondary"
										size="sm"
										title="Take this symbol out of the picker; anything bound to it keeps drawing it"
										:loading="busyId === symbol.id"
										@click="setRetired(symbol, true)"
									/>
									<ButtonLoading
										v-else
										text="Restore"
										variant="outline-secondary"
										size="sm"
										:loading="busyId === symbol.id"
										@click="setRetired(symbol, false)"
									/>
									<ButtonLoading
										text="Duplicate to my library"
										variant="outline-secondary"
										size="sm"
										:loading="copyingId === symbol.id"
										@click="copy(symbol)"
									/>
									<ButtonLoading
										text="Delete"
										variant="outline-danger"
										size="sm"
										title="Only a symbol nothing names may be deleted"
										:loading="deletingId === symbol.id"
										@click="remove(symbol)"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="card">
			<div class="card-body">
				<h2 class="h5 mb-1">Subject kinds</h2>
				<p class="text-body-secondary small">
					The default a subject draws with when nothing more specific applies. A
					tag outranks its kind's default: binding a symbol to a tag styles
					every Site that tag leads on, whatever the kind says.
				</p>

				<div v-if="catalogLoading" class="text-center py-3">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div
					v-else-if="!catalog || catalog.kinds.length === 0"
					class="text-muted py-3"
				>
					The subject-kind registry answered no kinds.
				</div>

				<div v-else class="table-responsive">
					<table class="table align-middle mb-0">
						<thead class="table-light">
							<tr>
								<th scope="col">Kind</th>
								<th scope="col">District default</th>
								<th scope="col">Draws with</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="kind in catalog.kinds" :key="kind.code">
								<td>
									<div class="fw-semibold">{{ kind.label }}</div>
									<code class="small">{{ kind.code }}</code>
									<span
										v-if="!kind.taggable"
										class="badge text-bg-light text-dark ms-1"
										title="No map layer consumes this kind in v1"
									>
										not used in v1
									</span>
								</td>
								<td>
									<div class="d-flex align-items-center gap-2">
										<span
											class="symbol-swatch"
											:class="{
												'symbol-swatch--none': !kind.district.symbol_id,
											}"
											:style="
												kind.district.symbol_id
													? styleCss(kind.district)
													: undefined
											"
											aria-hidden="true"
										></span>
										<span class="small">
											{{ kind.district.name ?? "The product default" }}
										</span>
										<span
											class="badge"
											:class="sourceBadgeClass(kind.district.source)"
										>
											{{ sourceLabel(kind.district.source) }}
										</span>
									</div>
								</td>
								<td>
									<SymbolPicker
										:model-value="kindSymbolID(kind)"
										:symbols="districtSymbols"
										label-for-null="The product default"
										@update:model-value="
											(symbolID) => setKindDefault(kind, symbolID)
										"
									/>
									<!-- §4.3's warning, beside the field it is about: this is
									     the one write that can retire existing information. -->
									<p class="form-text mb-0 kind-warning">
										A fill here replaces the assignment colour on the map for
										every Site this reaches.
									</p>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<ToastNotification
			v-model:show="toast.show"
			:title="toast.title"
			:message="toast.message"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import IconUpload from "@/components/symbology/IconUpload.vue";
import SymbolPicker from "@/components/symbology/SymbolPicker.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { SITE_FILL_COLOR } from "@/lib/mapReviewLayers";
import { styleCss, symbolIconURL } from "@/lib/symbology";
import type {
	Symbol,
	SymbolShape,
	SymbolStyle,
	SymbologyCatalog,
	SymbologyKind,
} from "@/type/api";

/** The marker vocabulary the schema's enum admits (§1 step 1). A closed list
 * because every value has a drawing rule in ts/lib/symbology.ts. */
const SHAPES: SymbolShape[] = [
	"circle",
	"square",
	"triangle",
	"diamond",
	"pin",
	"hexagon",
	"star",
];

const symbols = ref<Symbol[]>([]);
const catalog = ref<SymbologyCatalog | null>(null);
const loading = ref(true);
const catalogLoading = ref(true);
const error = ref("");
const showRetired = ref(false);
const busyId = ref<number | null>(null);
const copyingId = ref<number | null>(null);
const deletingId = ref<number | null>(null);
const saving = ref(false);
const editorOpen = ref(false);
const editorID = ref<number | null>(null);
const toast = reactive({ show: false, title: "", message: "" });

const form = reactive({
	name: "",
	description: "",
	shape: "circle" as SymbolShape,
	size: 0,
	useFill: true,
	fillColor: SITE_FILL_COLOR,
	fillOpacity: 0.35,
	useStroke: true,
	strokeColor: SITE_FILL_COLOR,
	strokeOpacity: 0.9,
	strokeWidth: 1,
	iconUuid: null as string | null,
	private: false,
});

const districtSymbols = computed(() =>
	symbols.value.filter((symbol) => symbol.owner === "district"),
);

const visibleSymbols = computed(() =>
	showRetired.value
		? symbols.value
		: symbols.value.filter((symbol) => !symbol.retired),
);

function showToast(title: string, message: string) {
	toast.title = title;
	toast.message = message;
	toast.show = true;
}

function errorMessage(e: any): string {
	return e?.response?.data?.message || e?.message || "The request failed.";
}

/** A number input left blank reads NaN; the server's own default is what such a
 * field should mean, so the payload states a number. */
function numberOr(value: number, fallback: number): number {
	return Number.isFinite(value) ? value : fallback;
}

function sourceLabel(source: SymbolStyle["source"]): string {
	if (source === "district") return "district";
	if (source === "user") return "yours";
	return "built-in";
}

function sourceBadgeClass(source: SymbolStyle["source"]): string {
	if (source === "district") return "text-bg-primary";
	if (source === "user") return "text-bg-info";
	return "text-bg-secondary";
}

/** The symbol the district has bound to a kind, readable from the catalog: the
 * `district` arm is arms 4→5, so its source says whether arm 4 exists at all. */
function kindSymbolID(kind: SymbologyKind): number | null {
	return kind.district.source === "district" ? kind.district.symbol_id : null;
}

async function loadSymbols() {
	loading.value = symbols.value.length === 0;
	try {
		symbols.value = await apiClient.JSONGet<Symbol[]>(
			"/api/symbol?include_retired=1",
		);
	} catch (e: any) {
		error.value = errorMessage(e);
	} finally {
		loading.value = false;
	}
}

async function loadCatalog() {
	catalogLoading.value = catalog.value === null;
	try {
		catalog.value = await apiClient.JSONGet<SymbologyCatalog>(
			"/api/symbology/catalog",
		);
	} catch (e: any) {
		error.value = errorMessage(e);
	} finally {
		catalogLoading.value = false;
	}
}

async function loadAll() {
	error.value = "";
	await Promise.all([loadSymbols(), loadCatalog()]);
}

function resetForm() {
	form.name = "";
	form.description = "";
	form.shape = "circle";
	form.size = 0;
	form.useFill = true;
	form.fillColor = SITE_FILL_COLOR;
	form.fillOpacity = 0.35;
	form.useStroke = true;
	form.strokeColor = SITE_FILL_COLOR;
	form.strokeOpacity = 0.9;
	form.strokeWidth = 1;
	form.iconUuid = null;
	form.private = false;
}

function openNew() {
	resetForm();
	editorID.value = null;
	editorOpen.value = true;
}

function openRestyle(symbol: Symbol) {
	editorID.value = symbol.id;
	form.name = symbol.name;
	form.description = symbol.description;
	form.shape = symbol.shape;
	form.size = symbol.size ?? 0;
	form.useFill = symbol.fill_color !== null;
	form.fillColor = symbol.fill_color ?? SITE_FILL_COLOR;
	form.fillOpacity = symbol.fill_opacity;
	form.useStroke = symbol.stroke_color !== null;
	form.strokeColor = symbol.stroke_color ?? SITE_FILL_COLOR;
	form.strokeOpacity = symbol.stroke_opacity;
	form.strokeWidth = symbol.stroke_width;
	form.iconUuid = symbol.icon_uuid;
	form.private = symbol.owner === "user";
	editorOpen.value = true;
}

function closeEditor() {
	editorOpen.value = false;
	editorID.value = null;
	resetForm();
}

async function saveSymbol() {
	if (saving.value) return;
	const name = form.name.trim();
	if (name === "") {
		showToast("Name required", "A symbol needs a name.");
		return;
	}
	// Both writes state every style field: "" clears a colour, 0 clears the size,
	// and the server validates the rest (a symbol must state a fill, an outline
	// or an icon).
	const body: Record<string, unknown> = {
		name,
		description: form.description,
		shape: form.shape,
		fill_color: form.useFill ? form.fillColor : "",
		stroke_color: form.useStroke ? form.strokeColor : "",
		fill_opacity: numberOr(form.fillOpacity, 0.35),
		stroke_opacity: numberOr(form.strokeOpacity, 0.9),
		stroke_width: numberOr(form.strokeWidth, 1),
		size: numberOr(form.size, 0),
		icon_uuid: form.iconUuid ?? "",
	};
	saving.value = true;
	try {
		if (editorID.value === null) {
			body.private = form.private;
			await apiClient.JSONPost<Symbol>("/api/symbol", body);
			showToast("Symbol created", `“${name}” is in the library.`);
		} else {
			await apiClient.JSONPut<Symbol>(`/api/symbol/${editorID.value}`, body);
			showToast("Symbol saved", `“${name}” is restyled.`);
		}
		closeEditor();
		// The library's usage counts and the catalog's resolved styles both move
		// when a symbol changes, so both are re-read.
		await loadAll();
	} catch (e: any) {
		showToast("Could not save the symbol", errorMessage(e));
	} finally {
		saving.value = false;
	}
}

async function setRetired(symbol: Symbol, retire: boolean) {
	if (busyId.value !== null) return;
	busyId.value = symbol.id;
	try {
		await apiClient.JSONPost(
			`/api/symbol/${symbol.id}/${retire ? "retire" : "restore"}`,
		);
		showToast(
			retire ? "Symbol retired" : "Symbol restored",
			retire
				? `“${symbol.name}” is out of the picker; anything bound to it keeps drawing it.`
				: `“${symbol.name}” is back in the picker.`,
		);
		await loadAll();
	} catch (e: any) {
		showToast(
			retire ? "Could not retire the symbol" : "Could not restore the symbol",
			errorMessage(e),
		);
	} finally {
		busyId.value = null;
	}
}

async function copy(symbol: Symbol) {
	if (copyingId.value !== null) return;
	copyingId.value = symbol.id;
	try {
		const created = await apiClient.JSONPost<Symbol>(
			`/api/symbol/${symbol.id}/copy`,
			{},
		);
		showToast(
			"Copied",
			`“${created.name}” is in your private library — yours to adjust.`,
		);
		await loadSymbols();
	} catch (e: any) {
		showToast("Could not copy the symbol", errorMessage(e));
	} finally {
		copyingId.value = null;
	}
}

async function remove(symbol: Symbol) {
	if (deletingId.value !== null) return;
	if (
		!confirm(
			`Delete the symbol “${symbol.name}”? A symbol anything still names is refused, so this only removes one that is unused.`,
		)
	)
		return;
	deletingId.value = symbol.id;
	try {
		await apiClient.JSONDelete(`/api/symbol/${symbol.id}`);
		showToast("Symbol deleted", `“${symbol.name}” is gone from the library.`);
		await loadAll();
	} catch (e: any) {
		// The 409 names what still uses it — the operator's only way to act on it.
		showToast("Could not delete the symbol", errorMessage(e));
	} finally {
		deletingId.value = null;
	}
}

/** One kind's district default: PUT binds, the empty choice unbinds. */
async function setKindDefault(kind: SymbologyKind, symbolID: number | null) {
	try {
		if (symbolID === null) {
			await apiClient.JSONDelete(`/api/symbology/kind/${kind.code}`);
			showToast(
				"Default removed",
				`${kind.label} draws with the product default again.`,
			);
		} else {
			await apiClient.JSONPut(`/api/symbology/kind/${kind.code}`, {
				symbol_id: symbolID,
			});
			showToast("Default saved", `${kind.label} draws with that symbol.`);
		}
		await loadCatalog();
	} catch (e: any) {
		showToast("Could not change the default", errorMessage(e));
		await loadCatalog();
	}
}

onMounted(loadAll);
</script>

<style scoped>
.symbol-swatch {
	display: inline-block;
	width: 2rem;
	height: 2rem;
	border-radius: 50%;
	flex: 0 0 auto;
}

.symbol-swatch--none {
	background: transparent;
	border: 1px dashed var(--bs-secondary-color);
}

.symbol-icon {
	width: 20px;
	height: 20px;
	object-fit: contain;
}

.fill-opacity-input {
	width: 5rem;
}

.kind-warning {
	max-width: 28rem;
}
</style>
